import { Flex, Heading, Table } from '@radix-ui/themes';
import { CommonDetails } from 'components/common/details';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CHART_COLORS } from 'enums/charts.enums';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { PitchType } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IGatherSummary } from 'lib_ts/interfaces/modelling/i-gather-shot-data';
import { first } from 'lodash';
import { FC, useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';
import {
  NameType,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent';

type HistMode = 'target_ms_group_size';

interface IHistogramData {
  type: string;
  groups: number;
  shots: number;
}

const getTitle = (mode: HistMode) => {
  switch (mode) {
    case 'target_ms_group_size': {
      return '# Unique Pitches by Type';
    }

    default: {
      return `${mode} by Type`;
    }
  }
};

const formatData = (
  mode: HistMode,
  groups: IGatherSummary[]
): IHistogramData[] => {
  switch (mode) {
    case 'target_ms_group_size': {
      const byType = ArrayHelper.groupByFn(
        groups,
        (g) => g.pitch?.type ?? PitchType.None
      );

      const output = Object.keys(byType).map((key) => {
        const summaries = byType[key] ?? [];

        const o: IHistogramData = {
          type: key,
          groups: summaries.length ?? 0,
          shots: summaries.reduce((total, g) => total + g.shot_ids.length, 0),
        };

        return o;
      });

      return output;
    }

    default: {
      return [];
    }
  }
};

const CustomTooltip = ({
  active,
  payload,
}: TooltipProps<ValueType, NameType>) => {
  if (!active) {
    return null;
  }

  if (!payload) {
    return null;
  }

  if (payload.length === 0) {
    return null;
  }

  const data: IHistogramData = first(payload)?.payload;

  if (!data) {
    return null;
  }

  return (
    <div className="custom-tooltip">
      Unique Pitches: {data.groups}
      <br />
      Total Shots: {data.shots}
    </div>
  );
};

interface IProps {
  mode: HistMode;
  groups: IGatherSummary[];
}

export const HistogramGroup: FC<IProps> = (props) => {
  const data = useMemo(
    () => formatData(props.mode, props.groups),
    [props.mode, props.groups]
  );

  const title = useMemo(() => getTitle(props.mode), [props.mode]);
  return (
    <ErrorBoundary identifier="HistogramGroup">
      <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
        <Heading size={RADIX.HEADING.SIZE.MD}>{title}</Heading>

        <ResponsiveContainer width="100%" height={200}>
          <BarChart
            data={data}
            margin={{
              top: 20,
              bottom: 20,
            }}
          >
            <CartesianGrid />

            <XAxis
              name="Pitch Type"
              type="category"
              dataKey="type"
              interval={0}
            />

            <YAxis name="Count" type="number" dataKey="groups" />

            <Tooltip content={<CustomTooltip />} />

            <Bar name="Count" dataKey="groups" fill={CHART_COLORS[0]} />
          </BarChart>
        </ResponsiveContainer>

        <CommonDetails summary="Toggle Tallies">
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Pitch Type</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Groups</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Shots</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {data.map((m) => (
                <Table.Row key={m.type}>
                  <Table.Cell>{m.type}</Table.Cell>
                  <Table.Cell>{m.groups}</Table.Cell>
                  <Table.Cell>{m.shots}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </CommonDetails>
      </Flex>
    </ErrorBoundary>
  );
};
